import { useCallback, useContext } from 'react';
import { Menu, Submenu, useRootMenuContext } from '@shared/components/Menu';
import { Confirm } from '@shared/components/Modal';
import { useToast } from '@shared/components/Toast';
import { useDeleteEntitiesMutation } from '@shared/scenes/cards/entityBulkActions/useDeleteWorkflowEntities';
import { i18n } from '@shared/util/i18n';
import { ChannelPermissionsContext } from '@shared/util/permissions';

interface Props {
  entityId: string;
  workflowTemplateId: string;
  onDelete?: VoidFunction;
}

export function DeleteSubmenu({ entityId, workflowTemplateId, onDelete }: Props) {
  const { align, close } = useRootMenuContext();
  const { canDeleteCard } = useContext(ChannelPermissionsContext);
  const deleteEntities = useDeleteEntitiesMutation(workflowTemplateId);
  const toast = useToast();

  const remove = useCallback(async () => {
    try {
      await deleteEntities([entityId]);
      toast({
        message: i18n('card.details.deleteCompleted'),
      });
      close();
      onDelete?.();
    } catch (error) {
      console.error(error);
      toast({
        style: 'warning',
        message: i18n('errors.somethingWentWrong'),
      });
    }
  }, [close, deleteEntities, entityId, onDelete, toast]);

  if (!canDeleteCard.enabled) {
    return null;
  }

  return (
    <Submenu align={align} closeOnSelect={false}>
      <Submenu.Trigger>
        <Menu.Item label={i18n('common.delete')} danger />
      </Submenu.Trigger>
      <Submenu.Content testID="delete-card-sheet">
        <Confirm
          inline
          danger
          title={i18n('card.details.deleteConfirmation.title')}
          message={i18n('card.details.deleteConfirmation.message')}
          confirmLabel={i18n('card.details.deleteConfirmation.confirm')}
          onConfirm={remove}
          onCancel={close}
        />
      </Submenu.Content>
    </Submenu>
  );
}
